import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export default function ProposalView() {
     const { token } = useParams();
     const [proposal, setProposal] = useState(null);
     const [loading, setLoading] = useState(true);
     const [accepting, setAccepting] = useState(false);
     const [accepted, setAccepted] = useState(false);
     const canvasRef = useRef(null);
     const drawingRef = useRef(false);
     const hasDrawnRef = useRef(false);

     useEffect(() => {
          loadProposal();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [token]);

     async function loadProposal() {
          const { data } = await supabase
               .from('proposals')
               .select('*')
               .eq('token', token)
               .single();

          if (data) {
               setProposal(data);
               if (data.status === 'accepted') setAccepted(true);
               // Mark as viewed
               if (data.status === 'sent') {
                    await supabase
                         .from('proposals')
                         .update({ status: 'viewed', updated_at: new Date().toISOString() })
                         .eq('id', data.id);
               }
          }
          setLoading(false);
     }

     const initCanvas = useCallback((canvas) => {
          if (!canvas) return;
          canvasRef.current = canvas;
          const ctx = canvas.getContext('2d');
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';

          function getPos(e) {
               const rect = canvas.getBoundingClientRect();
               const touch = e.touches ? e.touches[0] : e;
               return {
                    x: (touch.clientX - rect.left) * (canvas.width / rect.width),
                    y: (touch.clientY - rect.top) * (canvas.height / rect.height),
               };
          }

          function startDraw(e) {
               e.preventDefault();
               drawingRef.current = true;
               hasDrawnRef.current = true;
               const pos = getPos(e);
               ctx.beginPath();
               ctx.moveTo(pos.x, pos.y);
          }

          function draw(e) {
               if (!drawingRef.current) return;
               e.preventDefault();
               const pos = getPos(e);
               ctx.lineTo(pos.x, pos.y);
               ctx.stroke();
          }

          function stopDraw() {
               drawingRef.current = false;
          }

          canvas.addEventListener('mousedown', startDraw);
          canvas.addEventListener('mousemove', draw);
          canvas.addEventListener('mouseup', stopDraw);
          canvas.addEventListener('mouseleave', stopDraw);
          canvas.addEventListener('touchstart', startDraw, { passive: false });
          canvas.addEventListener('touchmove', draw, { passive: false });
          canvas.addEventListener('touchend', stopDraw);
     }, []);

     function clearSignature() {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          hasDrawnRef.current = false;
     }

     async function handleAccept() {
          if (!hasDrawnRef.current) {
               alert('Please sign above before accepting.');
               return;
          }
          setAccepting(true);
          const signatureData = canvasRef.current.toDataURL('image/png');

          const res = await fetch(`${SUPABASE_URL}/functions/v1/accept-proposal`, {
               method: 'POST',
               headers: {
                    'Content-Type': 'application/json',
                    apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
               },
               body: JSON.stringify({ token, signature_data: signatureData }),
          });

          if (res.ok) {
               setAccepted(true);
          } else {
               alert('Error accepting proposal. Please try again.');
          }
          setAccepting(false);
     }

     if (loading) {
          return (
               <div className='min-h-screen bg-black flex items-center justify-center'>
                    <p className='text-white/60'>Loading proposal...</p>
               </div>
          );
     }

     if (!proposal) {
          return (
               <div className='min-h-screen bg-black flex items-center justify-center'>
                    <p className='text-white/60'>Proposal not found.</p>
               </div>
          );
     }

     const lineItems = proposal.line_items || [];

     return (
          <div className='min-h-screen bg-black text-white'>
               <div className='max-w-2xl mx-auto px-4 py-12'>
                    {/* Header */}
                    <div className='text-center mb-8'>
                         <h1 className='text-yellow text-2xl font-bold'>Ripout Rentals</h1>
                         <p className='text-white/40 text-sm mt-1'>Proposal {proposal.proposal_number}</p>
                    </div>

                    {/* Proposal Details */}
                    <div className='bg-darker rounded-xl border border-dark p-6 mb-6'>
                         <div className='flex justify-between mb-6'>
                              <div>
                                   <p className='text-white/40 text-sm'>Prepared For</p>
                                   <p className='text-white font-bold'>{proposal.customer_name}</p>
                                   <p className='text-white/60 text-sm'>{proposal.customer_email}</p>
                              </div>
                              {proposal.valid_until && (
                                   <div className='text-right'>
                                        <p className='text-white/40 text-sm'>Valid Until</p>
                                        <p className='text-white'>{proposal.valid_until}</p>
                                   </div>
                              )}
                         </div>

                         {/* Line Items */}
                         <table className='w-full text-sm mb-6'>
                              <thead>
                                   <tr className='border-b border-dark text-white/40'>
                                        <th className='text-left py-2'>Description</th>
                                        <th className='text-right py-2'>Qty</th>
                                        <th className='text-right py-2'>Price</th>
                                        <th className='text-right py-2'>Total</th>
                                   </tr>
                              </thead>
                              <tbody className='divide-y divide-dark'>
                                   {lineItems.map((li, i) => (
                                        <tr key={i}>
                                             <td className='py-3 text-white'>{li.description}</td>
                                             <td className='py-3 text-white/60 text-right'>{li.quantity}</td>
                                             <td className='py-3 text-white/60 text-right'>
                                                  ${(li.unit_price_cents / 100).toFixed(2)}
                                             </td>
                                             <td className='py-3 text-white text-right'>
                                                  ${(li.total_cents / 100).toLocaleString()}
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                         </table>

                         {/* Totals */}
                         <div className='border-t border-dark pt-4 space-y-2 text-sm'>
                              <div className='flex justify-between text-white/60'>
                                   <span>Subtotal</span>
                                   <span>${(proposal.subtotal_cents / 100).toLocaleString()}</span>
                              </div>
                              {proposal.tax_cents > 0 && (
                                   <div className='flex justify-between text-white/60'>
                                        <span>Tax</span>
                                        <span>${(proposal.tax_cents / 100).toLocaleString()}</span>
                                   </div>
                              )}
                              <div className='flex justify-between text-white font-bold text-lg pt-2 border-t border-dark'>
                                   <span>Total</span>
                                   <span className='text-yellow'>
                                        ${(proposal.total_cents / 100).toLocaleString()}
                                   </span>
                              </div>
                         </div>

                         {proposal.notes && (
                              <div className='mt-6 pt-4 border-t border-dark'>
                                   <p className='text-white/40 text-sm mb-1'>Notes / Terms</p>
                                   <p className='text-white/80 text-sm whitespace-pre-wrap'>{proposal.notes}</p>
                              </div>
                         )}
                    </div>

                    {/* Signature & Accept */}
                    {accepted ? (
                         <div className='bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center'>
                              <h2 className='text-green-300 text-xl font-bold mb-2'>Proposal Accepted</h2>
                              {proposal.signature_data && (
                                   <div className='mt-4'>
                                        <p className='text-white/40 text-sm mb-2'>Signature</p>
                                        <img
                                             src={proposal.signature_data}
                                             alt='Signature'
                                             className='mx-auto bg-darker rounded-lg border border-dark'
                                             style={{ maxWidth: 300 }}
                                        />
                                   </div>
                              )}
                              {proposal.signed_at && (
                                   <p className='text-white/40 text-sm mt-3'>
                                        Signed on {new Date(proposal.signed_at).toLocaleString()}
                                   </p>
                              )}
                         </div>
                    ) : (
                         <div className='bg-darker rounded-xl border border-dark p-6'>
                              <h2 className='text-white font-bold mb-4'>Accept Proposal</h2>
                              <p className='text-white/60 text-sm mb-4'>
                                   Sign below to accept this proposal.
                              </p>
                              <div className='bg-dark rounded-lg border border-dark overflow-hidden mb-3'>
                                   <canvas
                                        ref={initCanvas}
                                        width={560}
                                        height={200}
                                        className='w-full cursor-crosshair'
                                        style={{ touchAction: 'none' }}
                                   />
                              </div>
                              <div className='flex items-center justify-between'>
                                   <button
                                        onClick={clearSignature}
                                        className='text-white/40 text-sm hover:text-white duration-300'
                                   >
                                        Clear Signature
                                   </button>
                                   <button
                                        onClick={handleAccept}
                                        disabled={accepting}
                                        className='px-8 py-3 bg-yellow text-black rounded-lg font-bold hover:bg-yellow/90 duration-300 disabled:opacity-50'
                                   >
                                        {accepting ? 'Accepting...' : 'Accept Proposal'}
                                   </button>
                              </div>
                         </div>
                    )}

                    {/* Footer */}
                    <div className='text-center mt-8'>
                         <a href='/' className='text-white/30 text-sm hover:text-white/60 duration-300'>
                              Ripout Rentals&trade; | Phoenix, Arizona
                         </a>
                    </div>
               </div>
          </div>
     );
}
